import { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Text,
  useColorModeValue,
  ScaleFade,
  Slide,
  Box,
  Icon,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  confirmColorScheme?: string;
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ComponentType;
}

export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmColorScheme = 'red',
  isLoading = false,
  loadingText,
  icon: IconComponent = FiAlertTriangle,
}: ConfirmationDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Color mode values
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');
  const contentBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const iconColor = useColorModeValue('orange.500', 'orange.300');

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      motionPreset="slideInBottom"
      isCentered
    >
      <AlertDialogOverlay
        bg={overlayBg}
        backdropFilter="blur(4px)"
      >
        <ScaleFade in={isOpen} initialScale={0.9}>
          <Slide in={isOpen} direction="bottom">
            <AlertDialogContent
              bg={contentBg}
              borderRadius={{ base: 'xl', md: '2xl' }}
              mx={{ base: 4, md: 0 }}
              my={{ base: 4, md: 0 }}
              maxW={{ base: '90vw', md: 'md' }}
              borderWidth="1px"
              borderColor={borderColor}
              shadow="2xl"
              overflow="hidden"
              position="relative"
            >
              {/* Animated top border */}
              <Box
                h="4px"
                w="full"
                bgGradient={`linear(to-r, ${confirmColorScheme}.400, ${confirmColorScheme}.600)`}
                position="absolute"
                top="0"
                left="0"
                right="0"
              />

              <AlertDialogHeader
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="bold"
                pt={6}
                pb={4}
                px={{ base: 4, md: 6 }}
              >
                <Box display="flex" alignItems="center" gap={3}>
                  <Icon
                    as={IconComponent}
                    boxSize={{ base: 5, md: 6 }}
                    color={iconColor}
                  />
                  <Text>{title}</Text>
                </Box>
              </AlertDialogHeader>

              <AlertDialogBody
                fontSize={{ base: 'sm', md: 'md' }}
                px={{ base: 4, md: 6 }}
                pb={2}
              >
                <Text lineHeight="1.6">
                  {message}
                </Text>
              </AlertDialogBody>

              <AlertDialogFooter
                px={{ base: 4, md: 6 }}
                py={{ base: 4, md: 6 }}
                gap={{ base: 2, md: 3 }}
                flexDirection={{ base: 'column-reverse', sm: 'row' }}
              >
                <Button
                  ref={cancelRef}
                  onClick={onClose}
                  variant="outline"
                  size={{ base: 'md', md: 'md' }}
                  w={{ base: 'full', sm: 'auto' }}
                  borderRadius="lg"
                  _hover={{
                    transform: 'translateY(-1px)',
                    shadow: 'md',
                    borderColor: useColorModeValue('gray.400', 'gray.500')
                  }}
                  transition="all 0.2s ease"
                  disabled={isLoading}
                >
                  {cancelText}
                </Button>

                <Button
                  colorScheme={confirmColorScheme}
                  onClick={handleConfirm}
                  size={{ base: 'md', md: 'md' }}
                  w={{ base: 'full', sm: 'auto' }}
                  ml={{ base: 0, sm: 3 }}
                  borderRadius="lg"
                  isLoading={isLoading}
                  loadingText={loadingText}
                  _hover={{
                    transform: 'translateY(-1px)',
                    shadow: 'lg'
                  }}
                  _active={{
                    transform: 'translateY(0)',
                    shadow: 'md'
                  }}
                  transition="all 0.2s ease"
                  fontWeight="600"
                >
                  {confirmText}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </Slide>
        </ScaleFade>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}